import * as React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { LoginPage } from '@/features/auth';
import { ROUTES } from '@/shared/constants/routes';

import { StackLayout, TabLayout } from '../layouts/tab-layout';
import {
  RedirectIfAuthenticated,
  RequireAuth,
  RequireNewcomer,
  RequireOnboarded,
  SplashScreen,
} from './guards';

/**
 * Route-level code splitting.
 *
 * The login screen and the app shell are eager — they are on every session's
 * critical path. Everything else is loaded on demand so a resident never
 * downloads the management screens, and vice versa.
 */
const HomePage = React.lazy(() =>
  import('@/features/dashboard').then((m) => ({ default: m.HomePage })),
);
const PaymentsPage = React.lazy(() =>
  import('@/features/payments/pages/payments-page').then((m) => ({ default: m.PaymentsPage })),
);
const AccountPage = React.lazy(() =>
  import('@/features/account').then((m) => ({ default: m.AccountPage })),
);
const ProfilePage = React.lazy(() =>
  import('@/features/account').then((m) => ({ default: m.ProfilePage })),
);
const HelpPage = React.lazy(() =>
  import('@/features/account').then((m) => ({ default: m.HelpPage })),
);
const PrivacyPage = React.lazy(() =>
  import('@/features/account').then((m) => ({ default: m.PrivacyPage })),
);
const NotificationsPage = React.lazy(() =>
  import('@/features/notifications').then((m) => ({ default: m.NotificationsPage })),
);
const TransactionsPage = React.lazy(() =>
  import('@/features/payments/pages/transactions-page').then((m) => ({
    default: m.TransactionsPage,
  })),
);

// --- Onboarding ---
const WelcomePage = React.lazy(() =>
  import('@/features/onboarding').then((m) => ({ default: m.WelcomePage })),
);
const CreateComplexPage = React.lazy(() =>
  import('@/features/onboarding').then((m) => ({ default: m.CreateComplexPage })),
);
const CreateBuildingPage = React.lazy(() =>
  import('@/features/onboarding').then((m) => ({ default: m.CreateBuildingPage })),
);
const JoinBuildingPage = React.lazy(() =>
  import('@/features/onboarding').then((m) => ({ default: m.JoinBuildingPage })),
);
const PendingRequestPage = React.lazy(() =>
  import('@/features/onboarding').then((m) => ({ default: m.PendingRequestPage })),
);

// --- Payment flow ---
const PayChargePage = React.lazy(() =>
  import('@/features/payments').then((m) => ({ default: m.PayChargePage })),
);
const PayOverduePage = React.lazy(() =>
  import('@/features/payments').then((m) => ({ default: m.PayOverduePage })),
);
const PaymentResultPage = React.lazy(() =>
  import('@/features/payments').then((m) => ({ default: m.PaymentResultPage })),
);

// --- Management ---
const ManageOverviewPage = React.lazy(() =>
  import('@/features/buildings').then((m) => ({ default: m.ManageOverviewPage })),
);
const BuildingDetailPage = React.lazy(() =>
  import('@/features/buildings').then((m) => ({ default: m.BuildingDetailPage })),
);
const ComplexDetailPage = React.lazy(() =>
  import('@/features/complexes').then((m) => ({ default: m.ComplexDetailPage })),
);
const BuildingUnitsPage = React.lazy(() =>
  import('@/features/units').then((m) => ({ default: m.BuildingUnitsPage })),
);
const UnitDetailPage = React.lazy(() =>
  import('@/features/units').then((m) => ({ default: m.UnitDetailPage })),
);
const MembershipRequestsPage = React.lazy(() =>
  import('@/features/members').then((m) => ({ default: m.MembershipRequestsPage })),
);
const BuildingChargesPage = React.lazy(() =>
  import('@/features/charges').then((m) => ({ default: m.BuildingChargesPage })),
);
const MonthlyChargeDetailPage = React.lazy(() =>
  import('@/features/charges').then((m) => ({ default: m.MonthlyChargeDetailPage })),
);
const TopPayersPage = React.lazy(() =>
  import('@/features/reports').then((m) => ({ default: m.TopPayersPage })),
);
const PaymentReportPage = React.lazy(() =>
  import('@/features/reports').then((m) => ({ default: m.PaymentReportPage })),
);
const DemoPage = React.lazy(() => import('@/features/demo').then((m) => ({ default: m.DemoPage })));
const AssignManagerPage = React.lazy(() =>
  import('@/features/managers').then((m) => ({ default: m.AssignManagerPage })),
);

function NotFoundPage() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 px-6 text-center">
      <h1 className="text-2xl font-bold">صفحه پیدا نشد</h1>
      <p className="text-muted-foreground text-sm">آدرس واردشده در برنامه وجود ندارد.</p>
      <a href={ROUTES.root} className="text-primary text-sm font-medium">
        بازگشت به صفحه اصلی
      </a>
    </div>
  );
}

export function AppRoutes() {
  return (
    <React.Suspense fallback={<SplashScreen />}>
      <Routes>
        {/* Public */}
        <Route element={<RedirectIfAuthenticated />}>
          <Route path={ROUTES.login} element={<LoginPage />} />
        </Route>

        {/* The gateway returns the payer here in a bare browser context, so
            this must resolve without a session. */}
        <Route path={ROUTES.paymentCallback} element={<PaymentResultPage />} />

        {/* Authenticated */}
        <Route element={<RequireAuth />}>
          {/* Onboarding — only before the user has any role or membership.
              StackLayout supplies the app column so these forms never stretch
              across a desktop viewport. */}
          <Route element={<RequireNewcomer />}>
            <Route element={<StackLayout />}>
              <Route path={ROUTES.welcome} element={<WelcomePage />} />
              <Route path={ROUTES.onboardingComplex} element={<CreateComplexPage />} />
              <Route path={ROUTES.onboardingJoin} element={<JoinBuildingPage />} />
              <Route path={ROUTES.onboardingPending} element={<PendingRequestPage />} />
            </Route>
          </Route>

          <Route element={<StackLayout />}>
            {/* Also reachable later, for a manager adding another building */}
            <Route path={ROUTES.onboardingBuilding} element={<CreateBuildingPage />} />
            {/* Demo walkthrough. Outside the onboarding guard so it stays
                reachable whatever the demo account's standing turns out to be. */}
            <Route path={ROUTES.demo} element={<DemoPage />} />
          </Route>

          <Route element={<RequireOnboarded />}>
            {/* Main tabs */}
            <Route element={<TabLayout />}>
              <Route index element={<HomePage />} />
              <Route path={ROUTES.payments} element={<PaymentsPage />} />
              <Route path={ROUTES.account} element={<AccountPage />} />
            </Route>

            {/* Pushed screens */}
            <Route element={<StackLayout />}>
              <Route path={ROUTES.profile} element={<ProfilePage />} />
              <Route path={ROUTES.help} element={<HelpPage />} />
              <Route path={ROUTES.privacy} element={<PrivacyPage />} />
              <Route path={ROUTES.notifications} element={<NotificationsPage />} />
              <Route path={ROUTES.transactions} element={<TransactionsPage />} />

              <Route path={ROUTES.payChargePattern} element={<PayChargePage />} />
              <Route path={ROUTES.payOverdue} element={<PayOverduePage />} />

              <Route path={ROUTES.manage} element={<ManageOverviewPage />} />
              <Route path={ROUTES.manageComplexPattern} element={<ComplexDetailPage />} />
              <Route path={ROUTES.manageBuildingPattern} element={<BuildingDetailPage />} />
              <Route path={ROUTES.manageBuildingUnitsPattern} element={<BuildingUnitsPage />} />
              <Route
                path={ROUTES.manageBuildingRequestsPattern}
                element={<MembershipRequestsPage />}
              />
              <Route path={ROUTES.manageBuildingChargesPattern} element={<BuildingChargesPage />} />
              <Route path={ROUTES.manageBuildingTopPayersPattern} element={<TopPayersPage />} />
              <Route path={ROUTES.manageBuildingReportPattern} element={<PaymentReportPage />} />
              <Route path={ROUTES.manageBuildingManagersPattern} element={<AssignManagerPage />} />
              <Route
                path={ROUTES.manageMonthlyChargePattern}
                element={<MonthlyChargeDetailPage />}
              />
              <Route path={ROUTES.manageUnitPattern} element={<UnitDetailPage />} />
            </Route>
          </Route>
        </Route>

        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </React.Suspense>
  );
}
